// Narrow cross-feature chat-unread capability (#1753, mirrors #1748/#1751 D-2).
// Exposes ONLY the chunked per-request chat-unread summary hook and its stable
// React-Query key factories — reused by the requests feature (RequestsListPage,
// via the requests barrel) AND by CRM My Day. It is NOT a general requests
// bucket: the requests stores, list pages, adapters, filters, and transition
// machinery stay classified as the `requests` feature. The capability imports
// only `api` + `shared` + npm — never a business feature capsule, which is the
// whole point (it removes the crm → requests cross-feature edge).
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type {
  RequestChatUnreadSummaryItem,
  RequestChatUnreadSummaryResponse,
} from '@magimp/shared';
import { REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS } from '@magimp/shared';
import { fetchRequestChatUnreadSummary } from '../../../api/requests/index.js';

// WHY (#570): unread-summary is user-scoped server state — a manager and an
// admin loading the same row id receive different counts. The queryKey shape
// encodes the request-id set so loading additional pages of an infinite list
// refetches summary for the new ids without refetching for ids already cached.
// Stable-sorted to make the key identity-equal across re-renders with the same
// set. INVARIANT (#1664): the prefix `['request-chat-unread-summary']` is the
// realtime invalidation handle (`mutations.ts`, `realtime/invalidateRequestCaches`),
// so this shape is byte-for-byte load-bearing — do not change it.
export const chatUnreadSummaryKey = (requestIds: readonly string[]) =>
  ['request-chat-unread-summary', [...requestIds].sort()] as const;
export const chatUnreadSummaryPrefixKey = () => ['request-chat-unread-summary'] as const;

export interface UnreadSummaryEntry {
  unreadCount: number;
  isMarkedUnread: boolean;
}

const EMPTY_UNREAD_MAP: ReadonlyMap<string, UnreadSummaryEntry> = new Map();

/**
 * @aiPurpose chunked variant for arbitrary-length id sets — the wire
 *   endpoint caps each query at `REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS`
 *   (200), so the list page splits its loaded ids into ≤200-sized batches
 *   and exposes a single merged map. Each chunk caches independently
 *   under the same `chatUnreadSummary` key shape so realtime invalidation
 *   by `chatUnreadSummaryPrefix()` covers all chunks.
 * @aiInvariant chunks are derived from a stable-sorted id list so adding
 *   a new id only spawns a fresh query when it crosses a chunk boundary,
 *   not when it lands inside an already-fetched chunk.
 */
export function useRequestChatUnreadSummaries(
  requestIds: readonly string[],
  options: { enabled?: boolean } = {},
): {
  data: ReadonlyMap<string, UnreadSummaryEntry>;
  isLoading: boolean;
  isError: boolean;
  /** Re-run every chunk query (e.g. to retry after a failed batch). */
  refetch: () => void;
} {
  const chunks = useMemo<readonly string[][]>(() => {
    if (requestIds.length === 0) return [];
    const sorted = [...requestIds].sort();
    const out: string[][] = [];
    for (let i = 0; i < sorted.length; i += REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS) {
      out.push(sorted.slice(i, i + REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS));
    }
    return out;
  }, [requestIds]);

  const enabled = options.enabled ?? true;
  const queries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: chatUnreadSummaryKey(chunk),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchRequestChatUnreadSummary(chunk, { signal }),
      enabled: enabled && chunk.length > 0,
    })),
  });

  return useMemo(() => {
    const refetch = () => {
      for (const q of queries) void q.refetch();
    };
    if (queries.length === 0) {
      return { data: EMPTY_UNREAD_MAP, isLoading: false, isError: false, refetch };
    }
    const m = new Map<string, UnreadSummaryEntry>();
    for (const q of queries) {
      const items = (q.data as RequestChatUnreadSummaryResponse | undefined) ?? [];
      for (const item of items as readonly RequestChatUnreadSummaryItem[]) {
        m.set(item.requestId, {
          unreadCount: item.unreadCount,
          isMarkedUnread: item.isMarkedUnread,
        });
      }
    }
    return {
      data: m,
      isLoading: queries.some((q) => q.isLoading),
      isError: queries.some((q) => q.isError),
      refetch,
    };
  }, [queries]);
}
